import { observer } from "mobx-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import usePage from "../../../../components/layouts/base-page-layout/page-hook";
import EditorPageLayout from "../../../../components/layouts/editor-page/editor-page-layout";
import { ConfirmModal } from "../../../../components/modal/components/confirm-modal/confirm-modal";
import useNavigationBlocker from "../../../../hooks/navigation-blocker-hook";
import useValidationGroup from "../../../../hooks/validation-group/validation-group-hook";
import policyListStore from "../../stores/policy-list-store";
import PolicyDetailsCard from "./policy-details-card/policy-details-card";
import PolicyEligibilityCard from "./policy-eligibility-card/policy-eligibility-card";
import ListLayout from "../../../../components/layouts/list-layout/list-layout";
import { Toggle } from "../../../../components/inputs/toggle/toggle";
import RMAHandlingCard from "./policy-rma-handling-card/policy-rma-handling-card";
import PolicyAdditionalPaymentsCard from "./policy-additional-payments-card/policy-additional-payments-card";
import FontBody2 from "../../../../components/fonts/font-body2/font-body2";
import "./policy-editor.scss";
import { Tooltip } from "../../../../components/tooltip/tooltip";
import Icon from "../../../../components/icons/icon/icon";
import IconInfo from "../../../../components/icons/icon-info/icon-info";
import { FONT_COLOR } from "../../../../components/fonts/font-props";
import './policy-editor.scss';
import IconWarning from "../../../../components/icons/icon-warning/icon-warning";
import useExpandCollapse from "../../../../hooks/expand-collapse-hook";
import { Note, NOTE_TYPE } from "../../../../components/note/note";

const PolicyEditor: React.FC = observer(() => {
    const navigate = useNavigate();
    const params = useParams();
    const { setSubTitles } = usePage();
    const vGroup = useValidationGroup();
    const [isShowUnsaved, setIsShowUnsaved] = useState(false);
    const policyId = params.policyId;
    const policy = policyListStore.selectedPolicy;

    const expandCollapseStore = useExpandCollapse(false);

    useEffect(() => {
        policyListStore.setSelectedPolicyId(policyId);

        /** collapse all cards, except details - patch on mobx warning, we still need to understand the reason */
        ['details-card', 'eligibility-card', 'rma-handling-card', 'additional-payments-card'].forEach((card, index) => {
            expandCollapseStore.setExpanded(card, index === 0);
        });

        return () => {
            policyListStore.setSelectedPolicyId(undefined);
        }
    }, [policyId, expandCollapseStore]);

    useEffect(() => {
        setSubTitles(['Return Policy', policy?.name]);
    }, [setSubTitles, policy?.name]);

    const hasChanges = (): boolean => {
        return !!policy?.hasChanges();
    }

    const discardChanges = useCallback(() => {
        policy?.discardChanges();
    }, [policy]);

    const { retryNavigation, unblockNavigation } = useNavigationBlocker(
        () => hasChanges(),
        useCallback(() => setIsShowUnsaved(true), [setIsShowUnsaved]),
    );
    const [isShowSaveConfirm, setIsShowSaveConfirm] = useState(false);

    const onSaveClick = async () => {
        if (hasChanges() && policy?._hasAssignedProductCases && !isShowSaveConfirm) {
            setIsShowSaveConfirm(true);
            return;
        }

        setIsShowSaveConfirm(false);
        const promised = [];

        const currentId = policy?.id;
        if (policy?.hasChanges()) {
            promised.push(
                policy?.save()
            );
        }

        Promise.all(promised).then(() => {
            unblockNavigation();
            if (currentId !== policy?.id) {
                navigate({
                    pathname: `/policy-rules/${policy?.id}`,
                }, { replace: true });
            }
        });
    }

    const policyEnabled = policy?.isEnabled && policy?.isResolutionAllowed;
    const getPolicyEnabledToggle = () => {
        if (!policy) {
            return <></>;
        }

        const toggle = <Toggle label="Activate Policy Rule" checked={policyEnabled} disabled={!policy.isResolutionAllowed} onChange={(v) => policy.setIsEnabled(v)} />;

        if (!policy.isResolutionAllowed) {
            return (
                <Tooltip
                    content={<FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>This feature is not included in your subscription plan</FontBody2>}>
                    {toggle}
                    <Icon Component={IconInfo} />
                </Tooltip>
            )

        }
        return toggle;
    }

    const isSaveDisabled = policy?.isEditingConditions()
            || !hasChanges() || !vGroup.isValid() || policy?.isLoading();

    return (
        <>
            <EditorPageLayout
                toolbar={{
                    saveButton: {
                        hasChanges: hasChanges(),
                        onClick: () => onSaveClick(),
                        disabled: isSaveDisabled,
                        isValid: vGroup.isValid(),
                    },
                    content: <div className="policy-editor-enable-toggle">{getPolicyEnabledToggle()}</div>,
                    isLoading: !policy,
                    hasLocalesSelector: false,
                    expandAllButton: {
                        expanded: expandCollapseStore.isAllExpanded(),
                        onClick: expandCollapseStore.toggleAllExpanded
                    },
                }}
            >
                {policy &&
                    <ListLayout>
                        <PolicyDetailsCard policy={policy} validationGroup={vGroup} expandCollapseStore={expandCollapseStore} />
                        <PolicyEligibilityCard policy={policy} expandCollapseStore={expandCollapseStore} />
                        <RMAHandlingCard policy={policy} validationGroup={vGroup} expandCollapseStore={expandCollapseStore} />
	                    <PolicyAdditionalPaymentsCard policy={policy} validationGroup={vGroup} expandCollapseStore={expandCollapseStore} />
                    </ListLayout>
                }

            </EditorPageLayout>

            {isShowUnsaved &&
                <ConfirmModal
                    cancelButton={{
                        title: 'Cancel',
                        onClick: () => setIsShowUnsaved(false)
                    }}
                    okButton={{
                        title: 'Discard',
                        onClick: () => {
                            discardChanges();
                            setIsShowUnsaved(false);
                            retryNavigation();
                        }
                    }}
                />
            }
            {isShowSaveConfirm &&
                <ConfirmModal
                    title=""
                    text={
                        <div style={{ maxWidth: '470px', margin: '0 auto', padding: '8px 0' }}>
                            <Note
                                type={NOTE_TYPE.WARNING}
                                caption="Policy Changes Apply to New Returns Only"
                                body="Updates to your return policy will only apply to future return requests. Existing RMAs will continue to follow the policy that was active when they were created."
                            />
                        </div>
                    }
                    cancelButton={{
                        title: 'Discard',
                        onClick: () => {
                            setIsShowSaveConfirm(false);
                            discardChanges();
                        }
                    }}
                    okButton={{
                        title: 'Save Changes',
                        onClick: () => onSaveClick()
                    }}
                />
            }
            {policy?.showDisplayAsDisabledConfirm && (
                <ConfirmModal
                    title='Display a Disabled Refund to Payment Method Policy Rule'
                    text={<div className="display-as-disabled-confirm-modal-content">
                        <p>
                            <FontBody2>
                                You're trying to set the current policy rule, "{policy.name}" to be displayed as disabled when no Refund to Payment Method policy rules are eligible.
                            </FontBody2>
                        </p>
                        <p>
                            <FontBody2>
                                The policy rule "{policyListStore.displayAsDisabledRefundPolicy?.name}" is already selected to be displayed.
                            </FontBody2>
                        </p>
                        <p>
                            <FontBody2>
                                Are you sure you want to change which policy rule will be displayed as disabled?
                            </FontBody2>
                        </p>
                    </div>}
                    cancelButton={{
                        title: 'Cancel',
                        onClick: policy.rejectSetDisplayAsDisabled
                    }}
                    okButton={{
                        title: 'Change',
                        onClick: policy.confirmSetDisplayAsDisabled
                    }}
            />
            )}
            {!!policy?.sideEffect.settingName && (
                <ConfirmModal
                    title={`Policy Rule Editor - Changing the ${policy.sideEffect.settingName} `}
                    text={<div className="side-effect-confirm-modal-content">
                        <p>
                            <FontBody2>
                                You’re changing the {policy.sideEffect.settingName} to <strong>{policy.sideEffect.settingValue}</strong>. This conflicts with some of your existing settings.
                            </FontBody2>
                        </p>
                        <div className="warning-message">
                            <IconWarning color='var(--color-accent-4)' />
                            <FontBody2 color={FONT_COLOR.ACCENT4}>
                                If you apply this change, the conflicting settings will be deleted or reset.
                            </FontBody2>
                        </div>
                    </div>}
                    implicationText={<FontBody2>
                        Are you sure you want to change the {policy.sideEffect.settingName}?
                    </FontBody2>}
                    cancelButton={{
                        title: 'Cancel',
                        onClick: policy.cancelSideEffect
                    }}
                    okButton={{
                        title: 'Change',
                        onClick: () => policy.sideEffect.confirmSideEffect?.()
                    }}
            />
            )}
        </>
    );
});

export default PolicyEditor;
